import { useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { useAdminInstallPrompt } from "@/lib/adminPwa";

/**
 * Header chip that lets an admin install the dashboard as a PWA.
 *
 * - Android / desktop Chrome → fires the saved `beforeinstallprompt`.
 * - iOS Safari → opens a small instruction modal (Apple blocks auto-prompts).
 * - Hidden completely once the page is already running standalone.
 */
export default function AdminInstallButton() {
  const { canInstall, installed, isIos, install } = useAdminInstallPrompt();
  const [showIosHint, setShowIosHint] = useState(false);

  if (installed) return null;
  if (!canInstall && !isIos) return null; // nothing useful we can offer here

  const onClick = () => {
    if (isIos) setShowIosHint(true);
    else install();
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="admin-install-btn"
        className="hidden sm:inline-flex items-center gap-2 h-10 px-3 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors"
        aria-label="Install GC Admin app"
      >
        <Download className="w-3.5 h-3.5" />
        <span>Install app</span>
      </button>
      <button
        type="button"
        onClick={onClick}
        data-testid="admin-install-btn-mobile"
        className="sm:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-emerald-700 hover:bg-emerald-50"
        aria-label="Install GC Admin app"
      >
        <Download className="w-4 h-4" />
      </button>

      {showIosHint && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setShowIosHint(false)}
          data-testid="admin-install-ios-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowIosHint(false)}
              className="absolute top-3 right-3 h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
              aria-label="Close"
              data-testid="admin-install-ios-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 inline-flex items-center justify-center text-emerald-700">
                <Share2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Install on iPhone</div>
                <div className="text-xs text-slate-500">Add GC Admin to your home screen</div>
              </div>
            </div>
            <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside leading-relaxed">
              <li>
                Tap the <strong>Share</strong> button{" "}
                <span className="inline-block align-middle text-slate-500">
                  <Share2 className="w-4 h-4 inline" />
                </span>{" "}
                in Safari&apos;s toolbar.
              </li>
              <li>
                Scroll down and tap <strong>“Add to Home Screen”</strong>.
              </li>
              <li>
                Tap <strong>Add</strong> in the top-right.
              </li>
            </ol>
            <p className="mt-4 text-xs text-slate-500">
              Once installed, push notifications &amp; offline access work just
              like a native app.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
